/** Determines the operational status of a task based on its delegation, scheduling, and completion state. */
import { useMemo } from 'react';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { processRootTasks } from '@/features/tasks/utils/views/nextActionUtils';

/**
 * @typedef {import('@/types').Task} Task
 */

/**
 * Custom hook that determines the "next action" for every project and standalone task.
 *
 * A "next action" is defined as: the deepest uncompleted subtask in a branch that
 * does NOT have status 'Backlog'. If a task has no eligible children, the task itself
 * is the next action (unless it is on the Backlog or already completed).
 *
 * The returned state reflects how the next action is currently scheduled:
 * - 'waiting'  — delegated to another person (`task.person` is set). Takes priority over 'planned'.
 * - 'planned'  — scheduled for a specific date (`task.date` is set).
 * - 'default'  — not delegated and not scheduled.
 *
 * Tasks with status 'Backlog' are ignored at every level of the hierarchy.
 *
 * @returns {Map<string, {nextAction: Task, state: 'planned' | 'waiting' | 'default'}>}
 * A map keyed by root-task ID, containing each task's resolved next action and its state.
 */
export const useNextAction = () => {
  const { taskTree } = useTasksContext();

  return useMemo(() => {
    if (!taskTree || taskTree.length === 0) {
      return new Map();
    }

    const taskMap = new Map(taskTree.map((t) => [t.id, t]));
    const rootTasks = taskTree.filter(
      (task) => !task.parentId || !taskMap.has(task.parentId),
    );

    return processRootTasks(rootTasks);
  }, [taskTree]);
};
